import React, { useState, useEffect, memo, forwardRef } from 'react';
import { API_BASE } from '@/config';

interface RetryImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  /** 加载中显示的占位内容（可选） */
  fallback?: React.ReactNode;
  /** 加载失败显示的占位内容（可选） */
  errorFallback?: React.ReactNode;
}

/**
 * 带重试和加载状态的图片组件
 *
 * 解决 Cloudflare Pages 部署下 /assets/ 路径走 302 到 Render 后端，
 * 后端休眠冷启动 30-60 秒导致图片加载 pending 的问题。
 *
 * 策略：
 * 1. 首次尝试原始 URL（可能是 /assets/... 相对路径，走 Cloudflare 302）
 * 2. 失败后用后端完整 URL 重试（绕过 Cloudflare，直连 Render）
 * 3. 仍失败显示 errorFallback
 */
export const RetryImage = memo(forwardRef<HTMLImageElement, RetryImageProps>(({
  src,
  fallback,
  errorFallback,
  alt = '',
  className,
  ...rest
}, ref) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [currentSrc, setCurrentSrc] = useState(src);

  // src 变化时重置状态
  useEffect(() => {
    setStatus('loading');
    setCurrentSrc(src);
  }, [src]);

  const handleError = () => {
    // 首次失败：尝试用后端完整 URL 重试（绕过 Cloudflare 302）
    if (currentSrc === src && src.startsWith('/assets/')) {
      const backendUrl = `${API_BASE}${src}`;
      setCurrentSrc(backendUrl);
      setStatus('loading');
      return;
    }
    // 重试仍失败
    setStatus('error');
  };

  const handleLoad = () => {
    setStatus('loaded');
  };

  if (status === 'error') {
    return (
      <>
        {errorFallback ?? (
          <div className="w-full h-full flex items-center justify-center bg-zinc-900/50 text-white/30 text-xs">
            图片加载失败
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {status === 'loading' && (fallback ?? (
        // 默认骨架屏占位（避免节点显示空白）
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/50">
          <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        </div>
      ))}
      <img
        {...rest}
        ref={ref}
        src={currentSrc}
        alt={alt}
        className={className}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          ...(status === 'loading' ? { opacity: 0, position: 'absolute', inset: 0 } : {}),
          ...rest.style,
        }}
      />
    </>
  );
}));
